import isEqual from 'lodash/isEqual';
import isFunction from 'lodash/isFunction';
import { useRef } from 'react';
import { useDispatch } from 'react-redux';

import { BuiltInComponents } from 'craft/constants';
import { InsertMode, useEditor, useMutations, useSelection } from 'craft/hooks';
import { Resolver, SerializedNodeTree } from 'craft/types';

import { setFlowStep } from './actions';
import { useFlowState } from './selectors';

/**
 * Now used by both flow and conditional views
 * This basically functions as a manager for children which are shown conditionally
 */
export const useFlow = (nodeId?: string) => {
  // first, let's get the node
  const { actions, query } = useEditor();
  const { insertSerializedNodeTrees } = useMutations();

  const { getSerializedTree } = useSelection();
  const dispatch = useDispatch();
  const node = query.node(nodeId)?.get();
  const flowProps = node?.data?.props || {};
  // TODO: this is a bit hacky, we should really generalize it.
  const stateId = flowProps.stateId || flowProps.formId;
  const flowState = useFlowState(stateId);

  const latestStepIds = useRef<string[]>([]);

  const getStepIds = () => {
    const childNodes = query.node(nodeId)?.get()?.data?.nodes;

    const stepIds = (childNodes
      ?.map((nid) => {
        const childNode = query.node(nid)?.get();
        let stepId = childNode?.data?.props?.stepId;

        if (!stepId && node.data.name === BuiltInComponents.ConditionalView) {
          stepId = nid;
        }

        if (!stepId) {
          return undefined;
        }
        const shouldIgnoreFlowStep = (childNode?.data.type as Resolver<any>)?.flow
          ?.shouldIgnoreFlowStep;
        if (isFunction(shouldIgnoreFlowStep) && shouldIgnoreFlowStep(childNode)) {
          return undefined;
        }
        return stepId;
      })
      .filter((id) => !!id) || []) as string[];

    if (!isEqual(latestStepIds.current, stepIds)) {
      latestStepIds.current = stepIds;
      return stepIds;
    }

    return latestStepIds.current;
  };

  const getStepNames = () => {
    const childNodes = query.node(nodeId)?.get()?.data?.nodes || [];

    return childNodes.map((stepNodeId) => {
      const currentNode = query.node(stepNodeId);

      if (node.data.name === BuiltInComponents.ConditionalView) {
        return currentNode?.node?.data?.props.name;
      }

      return currentNode?.node?.data?.custom?.displayName || '';
    });
  };

  const onSetStepId = (stepId: string, newIndex?: number) => {
    const stepIndex = typeof newIndex !== 'undefined' ? newIndex : getStepIndex(stepId);
    dispatch(setFlowStep(stateId, stepId, stepIndex));
  };

  const getStepIndex = (stepId: string): number => {
    const stepIds = getStepIds();
    // we use max to handle the case in which stepId is not found, hence we would get -1.
    // with Max, we're always guaranteed to get at least 0.
    return Math.max(
      stepIds.findIndex((id) => id === stepId),
      0,
    );
  };

  const numberOfSteps = getStepIds().length;

  const getNextStepId = (stepId: string): string => {
    const stepIds = getStepIds();
    const stepIdIndex = getStepIndex(stepId);

    const nextIndex = stepIdIndex + 1 > numberOfSteps ? 0 : stepIdIndex + 1;
    return stepIds[nextIndex];
  };

  const getPreviousStepId = (stepId: string): string => {
    const stepIds = getStepIds();
    const stepIdIndex = getStepIndex(stepId);
    const previousIndex = stepIdIndex === 0 ? numberOfSteps - 1 : stepIdIndex - 1;
    return stepIds[previousIndex];
  };

  const onNextStep = () => {
    onSetStepId(getNextStepId(flowState?.stepId));
  };
  const onPreviousStep = () => {
    onSetStepId(getPreviousStepId(flowState?.stepId));
  };

  const onMoveStep = (originalIndex: number, newIndex: number) => {
    const moveNodeId = node.data.nodes[originalIndex];
    const moveStepId = getStepIds()[originalIndex];

    if (newIndex > originalIndex) {
      // if we're dragging down, place after hovered card
      actions.move(moveNodeId, node.id, newIndex + 1);
    } else {
      actions.move(moveNodeId, node.id, newIndex);
    }

    onSetStepId(moveStepId);
  };

  const onDuplicateStep = (stepId: string) => {
    const stepIndex = getStepIndex(stepId);
    const currentFlowStepId = query.node(nodeId).descendants()[stepIndex];
    const serializedTree = getSerializedTree(currentFlowStepId);

    insertSerializedNodeTrees([serializedTree], currentFlowStepId, InsertMode.NEXT_TO);

    onSetStepId(getNextStepId(stepId));
  };

  const onAddStep = (stepElement: SerializedNodeTree) => {
    if (!nodeId) {
      return;
    }

    const result = insertSerializedNodeTrees([stepElement], nodeId, InsertMode.INTO)[0];
    let newStepId = result.nodes[result.rootNodeId]?.data?.props?.stepId;

    if (!newStepId && node.data.name === BuiltInComponents.ConditionalView) {
      newStepId = result.rootNodeId;
    }

    onSetStepId(newStepId);
  };

  return {
    flowNode: node,
    index: flowState?.stepIndex || 0,
    activeStepId: flowState?.stepId || getStepIds()[0],
    stateId,
    stepIds: getStepIds(),
    stepNames: getStepNames(),
    getStepIndex,
    getNextStepId,
    getPreviousStepId,
    onSetStepId,
    onNextStep,
    onPreviousStep,
    onMoveStep,
    onDuplicateStep,
    onAddStep,
  };
};
